import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { io, type Socket } from 'socket.io-client';
import { toast } from 'sonner';
import { api, getAccessToken, refreshSession } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import {
  fetchNotificationsAfter,
  fetchUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  NOTIF_KEY,
  type AppNotification,
  type NotificationPage,
  type NotifyBox,
} from './api';
import { wakeGroupOf, type WakeGroup } from '@mt/shared';
import { announceNotification, announceOnOpen, installAnnounceUnlock, setAnnounceListener, shouldAnnounce, stopAnnounceRepeat } from './announce';
import { useScreenWake } from './screen-wake';

/**
 * Kết nối realtime (P05.7, ADR-020). Socket.IO chỉ là kênh BÁO; dữ liệu thật lấy từ API /notifications.
 *  - Kết nối bằng access token hiện tại; server ngắt khi token hết hạn → làm mới phiên rồi kết nối lại.
 *  - Mất kết nối: tự kết nối lại (1–30 s); kết nối lại xong thì lấy bù GET /notifications?after=<id cuối>.
 *  - Lưới an toàn: đếm chưa đọc mỗi 60 s.
 *  - Số đỏ = Khẩn + Trong ngày chưa đọc; Thông tin (Bản tin) đếm riêng, không số đỏ, không bật thông báo nổi (21/10/2026).
 */
export type RealtimeStatus = 'connected' | 'reconnecting' | 'disconnected';
export const STATUS_LABELS: Record<RealtimeStatus, string> = {
  connected: 'Đã kết nối',
  reconnecting: 'Đang kết nối lại',
  disconnected: 'Mất kết nối',
};

interface NotificationState {
  status: RealtimeStatus;
  unreadCount: number;
  infoUnreadCount: number;
  latest: AppNotification[];
  loading: boolean;
  open: (n: AppNotification) => void;
  markRead: (id: number) => Promise<void>;
  markAllRead: (box?: NotifyBox) => Promise<void>;
}

const Ctx = createContext<NotificationState | null>(null);
const LATEST = 40;
const isInfo = (n: Pick<AppNotification, 'level'>) => n.level === 'info';

/** Cache TanStack Query cần làm mới theo loại dữ liệu của thông báo. */
const KEYS_BY_ENTITY: Record<string, string[]> = {
  lead: ['leads'],
  opportunity: ['opportunities'],
  complaint: ['complaints'],
  quote: ['quotes'],
  asia_sync_run: ['asia'],
  outbox_event: ['admin-events'],
};
const KEYS_BY_TYPE: Record<string, string[]> = { FOLLOW_UP_DUE: ['follow-ups'] };

const mergeLatest = (cur: AppNotification[], incoming: AppNotification[]) => {
  const byId = new Map(cur.map((n) => [n.id, n]));
  for (const n of incoming) byId.set(n.id, n);
  return [...byId.values()].sort((a, b) => b.id - a.id).slice(0, LATEST);
};

/**
 * DEC-900: 1 thông báo nổi cho mỗi loại tin nhắc lặp (vd. "Kho còn N việc…") – tin mới thay tin cũ, không chồng 2 hộp.
 * Tin khác: cùng tiêu đề + nội dung cũng chỉ hiện 1 hộp.
 */
function toastId(n: AppNotification): string {
  return /Reminder$/.test(n.eventType) ? `notif:${n.eventType}` : `notif:${n.title}|${n.message ?? ''}`;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const { user, can } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [status, setStatus] = useState<RealtimeStatus>('reconnecting');
  const [unreadCount, setUnread] = useState(0);
  const [infoUnreadCount, setInfoUnread] = useState(0);
  const setCounts = useCallback((c: { unreadCount?: number; infoUnreadCount?: number }) => {
    if (typeof c.unreadCount === 'number') setUnread(c.unreadCount);
    if (typeof c.infoUnreadCount === 'number') setInfoUnread(c.infoUnreadCount);
  }, []);
  const [latest, setLatest] = useState<AppNotification[]>([]);
  const [loading, setLoading] = useState(true);
  const lastId = useRef(0);
  // Việc bắt buộc nhắc lại tới khi mở xem (DEC-671): tập id còn chưa đọc, cập nhật theo danh sách mới nhất
  const unreadIds = useRef(new Set<number>());
  const enabled = !!user && can('notification.read');

  const open = useCallback(
    (n: AppNotification) => {
      if (!n.isRead) void markNotificationRead(n.id).catch(() => undefined);
      setLatest((l) => l.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      if (!n.isRead) (isInfo(n) ? setInfoUnread : setUnread)((c) => Math.max(0, c - 1));
      if (n.link) navigate(n.link);
    },
    [navigate],
  );

  const onIncoming = useCallback(
    (items: AppNotification[], live: boolean) => {
      if (!items.length) return;
      lastId.current = Math.max(lastId.current, ...items.map((n) => n.id));
      setLatest((l) => mergeLatest(l, items));
      const keys = new Set<string>(NOTIF_KEY);
      for (const n of items) {
        for (const k of KEYS_BY_ENTITY[n.entityType ?? ''] ?? []) keys.add(k);
        for (const k of KEYS_BY_TYPE[n.type] ?? []) keys.add(k);
        // Chỉ Khẩn, hoặc Trong ngày gắn ưu tiên Cao, mới bật thông báo nổi; Bản tin thì không
        // DEC-671: việc giao / hoàn thành việc → chuông "Minh Tiến" + đọc to (theo cài đặt của máy này), kèm thông báo nổi để đọc lại
        const announce = live && !n.isRead && shouldAnnounce(n);
        if (announce) void announceNotification(n, () => unreadIds.current.has(n.id)).catch(() => undefined);
        if (live && !isInfo(n) && (n.level === 'urgent' || n.priority === 'HIGH' || n.priority === 'URGENT')) {
          const show = n.level === 'urgent' || n.priority === 'URGENT' ? toast.error : toast.warning;
          show(n.title, { id: toastId(n), description: n.message ?? undefined, duration: 8000, action: n.link ? { label: 'Mở', onClick: () => open(n) } : undefined });
        } else if (announce) {
          toast.info(n.title, { id: toastId(n), description: n.message ?? undefined, duration: 8000, action: n.link ? { label: 'Mở', onClick: () => open(n) } : undefined });
        }
      }
      keys.forEach((k) => void qc.invalidateQueries({ queryKey: [k] }));
    },
    [qc, open],
  );

  useEffect(() => {
    unreadIds.current = new Set(latest.filter((x) => !x.isRead).map((x) => x.id));
  }, [latest]);

  // Trình duyệt chỉ cho phát tiếng sau lần chạm đầu → mở khóa âm báo ở lần chạm / bấm phím đầu tiên (DEC-671)
  useEffect(() => {
    if (enabled) return installAnnounceUnlock();
  }, [enabled]);
  // Gọi đúng tên + Anh / Chị theo giới tính trong hồ sơ nhân viên; nhóm công → khung giữ màn sáng
  const driverOnly = can('fleet.driver') && !can('fleet.read') && !can('customer.read') && !can('quote.read');
  const [wakeGroup, setWakeGroup] = useState<WakeGroup | null>(null);
  useEffect(() => {
    if (!enabled) {
      setAnnounceListener(null);
      setWakeGroup(null);
      return;
    }
    setAnnounceListener({ fullName: user?.fullName ?? null, gender: null });
    setWakeGroup(wakeGroupOf(null, driverOnly));
    let alive = true;
    api
      .get<{ fullName: string; gender: string | null; workGroup: string | null }>('/notifications/voice-profile')
      .then((p) => {
        if (!alive) return;
        setAnnounceListener(p);
        setWakeGroup(wakeGroupOf(p.workGroup, driverOnly));
      })
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [enabled, user?.id, user?.fullName, driverOnly]);
  useScreenWake(wakeGroup);

  // DEC-671: bấm thông báo đẩy → đọc to ngay tin đó (cửa sổ đang mở nhận postMessage, mở mới có ?doc-tin=<id>)
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const readId = async (id: number) => {
      try {
        const p = await fetchNotificationsAfter(id - 1);
        const n = p.items.find((x) => x.id === id);
        if (alive && n) await announceOnOpen(n);
      } catch {
        /* không đọc được thì thôi, tin vẫn hiện trên màn */
      }
    };
    const url = new URL(window.location.href);
    const first = Number(url.searchParams.get('doc-tin'));
    if (first > 0) {
      url.searchParams.delete('doc-tin');
      window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      void readId(first);
    }
    const onMsg = (e: MessageEvent) => {
      const d = e.data as { type?: string; notificationId?: unknown } | null;
      if (d?.type === 'NAVIGATE' && typeof d.notificationId === 'number' && d.notificationId > 0) void readId(d.notificationId);
    };
    const sw = typeof navigator !== 'undefined' && 'serviceWorker' in navigator ? navigator.serviceWorker : null;
    sw?.addEventListener('message', onMsg);
    return () => {
      alive = false;
      sw?.removeEventListener('message', onMsg);
    };
  }, [enabled]);

  // Ref để socket không bị dựng lại mỗi lần đổi trang (navigate đổi theo vị trí)
  const incomingRef = useRef(onIncoming);
  useEffect(() => {
    incomingRef.current = onIncoming;
  }, [onIncoming]);

  useEffect(() => {
    if (!enabled) {
      setLatest([]);
      setUnread(0);
      setInfoUnread(0);
      lastId.current = 0;
      return;
    }
    let disposed = false;
    let failures = 0;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;

    // Tải danh sách ban đầu
    setLoading(true);
    api
      .get<NotificationPage>('/notifications', { limit: LATEST })
      .then((p) => {
        if (disposed) return;
        lastId.current = Math.max(lastId.current, ...p.items.map((n) => n.id), 0);
        setLatest((l) => mergeLatest(l, p.items));
        setCounts(p);
      })
      .catch(() => undefined)
      .finally(() => !disposed && setLoading(false));

    const socket: Socket = io({
      path: '/api/realtime/',
      transports: ['websocket'],
      auth: (cb) => cb({ token: getAccessToken() }),
      reconnectionDelay: 1000,
      reconnectionDelayMax: 30_000,
    });

    const resync = async () => {
      try {
        if (lastId.current > 0) {
          const p = await fetchNotificationsAfter(lastId.current);
          if (!disposed) incomingRef.current(p.items, false);
          if (!disposed) setCounts(p);
        } else {
          const c = await fetchUnreadCount();
          if (!disposed) setCounts({ unreadCount: c.count, infoUnreadCount: c.infoCount });
        }
      } catch {
        /* lần sau thử lại */
      }
    };

    // Bị server từ chối (token hết hạn…) hoặc chủ động ngắt: làm mới phiên rồi tự kết nối lại có giãn cách
    const reauth = () => {
      if (disposed || retryTimer) return;
      failures++;
      setStatus(failures >= 5 ? 'disconnected' : 'reconnecting');
      const delay = Math.min(30_000, 1000 * 2 ** Math.min(failures - 1, 5));
      retryTimer = setTimeout(async () => {
        retryTimer = undefined;
        if (disposed) return;
        await refreshSession().catch(() => null);
        if (!disposed && getAccessToken()) socket.connect();
      }, delay);
    };

    socket.on('connect', () => {
      failures = 0;
      setStatus('connected');
      void resync();
    });
    socket.on('disconnect', (reason) => {
      if (disposed) return;
      if (reason === 'io server disconnect') reauth();
      else setStatus('reconnecting');
    });
    socket.on('connect_error', () => {
      // Lỗi xác thực ở middleware: client không tự thử lại → tự làm mới token rồi kết nối lại.
      // Lỗi mạng: client tự thử lại; vẫn đếm để chuyển sang "Mất kết nối" khi kéo dài.
      if (!socket.active) reauth();
      else {
        failures++;
        setStatus(failures >= 5 ? 'disconnected' : 'reconnecting');
      }
    });
    socket.on('notification:new', (n: AppNotification) => {
      // Leo thang: cùng id, Bản tin → chuông (escalatedAt) – chuyển số từ Bản tin sang số đỏ
      if (n.escalatedAt && !n.isRead) setInfoUnread((c) => Math.max(0, c - 1));
      incomingRef.current([n], true);
      if (!n.isRead) (isInfo(n) ? setInfoUnread : setUnread)((c) => c + 1);
      // Anh Tiến 20/10/2026 "tất cả realtime": thông báo kho / giao hàng → màn Soạn hàng tải lại ngay
      if (n.type.startsWith('DELIVERY')) void qc.invalidateQueries({ queryKey: ['pick-queue'] });
    });
    // Hộp thư Zalo (CR-014): chỉ là tín hiệu, khung hộp thư tự tải lại qua API
    socket.on('zalo:changed', () => void qc.invalidateQueries({ queryKey: ['zalo'] }));
    // Trao đổi trên việc (DEC-323): khung trao đổi đang mở tự tải lại ngay
    socket.on('work:comment', (p: { orderId: number }) => void qc.invalidateQueries({ queryKey: ['work-orders', 'comments', p.orderId] }));
    // Đàm phán NCC (DEC-542): NCC nhắn / báo giá trên cổng, AI dịch xong → hộp đàm phán tự tải lại
    socket.on('negotiation:message', () => void qc.invalidateQueries({ queryKey: ['negotiations'] }));
    // Bảng tin nội bộ: bài / thích / bình luận mới → bảng tin tự tải lại
    socket.on('feed:changed', () => void qc.invalidateQueries({ queryKey: ['feed'] }));
    // Vòng quay may mắn: máy khác đang mở cùng đợt quay chạy hoạt ảnh theo kết quả máy chủ vừa quay
    socket.on('lucky:changed', () => void qc.invalidateQueries({ queryKey: ['lucky-wheel'] }));
    socket.on('lucky:spin', (p: unknown) => window.dispatchEvent(new CustomEvent('lucky:spin', { detail: p })));
    socket.on('notification:read', (p: { ids?: number[]; all?: boolean; box?: NotifyBox | null; unreadCount: number; infoUnreadCount?: number }) => {
      setCounts(p);
      const hit = (x: AppNotification) => p.all || p.ids?.includes(x.id) || (p.box === 'info' ? isInfo(x) : p.box === 'action' ? !isInfo(x) : false);
      setLatest((l) => l.map((x) => (hit(x) ? { ...x, isRead: true } : x)));
      void qc.invalidateQueries({ queryKey: [...NOTIF_KEY] });
    });

    const poll = setInterval(() => {
      fetchUnreadCount()
        .then((c) => !disposed && setCounts({ unreadCount: c.count, infoUnreadCount: c.infoCount }))
        .catch(() => undefined);
    }, 60_000);

    return () => {
      disposed = true;
      stopAnnounceRepeat();
      clearInterval(poll);
      clearTimeout(retryTimer);
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [enabled, user?.id, qc, setCounts]);

  const markRead = useCallback(async (id: number) => {
    const r = await markNotificationRead(id);
    setCounts(r);
    setLatest((l) => l.map((x) => (x.id === id ? { ...x, isRead: true } : x)));
  }, []);

  const markAllRead = useCallback(
    async (box?: NotifyBox) => {
      const r = await markAllNotificationsRead(box);
      setCounts(r);
      setLatest((l) => l.map((x) => (!box || (box === 'info') === isInfo(x) ? { ...x, isRead: true } : x)));
      void qc.invalidateQueries({ queryKey: [...NOTIF_KEY] });
    },
    [qc, setCounts],
  );

  const value = useMemo<NotificationState>(
    () => ({ status, unreadCount, infoUnreadCount, latest, loading, open, markRead, markAllRead }),
    [status, unreadCount, infoUnreadCount, latest, loading, open, markRead, markAllRead],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useNotifications() {
  const v = useContext(Ctx);
  if (!v) throw new Error('useNotifications phải nằm trong NotificationProvider');
  return v;
}
